import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { CUSTOMER, PROVIDER } from '../util/types';
import * as log from '../util/log';
import * as fbq from '../lib/fpixel.js';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import { transitions } from '../transactions/transactionProcessBooking';
import { HOST_BOOKING_LAST_TRANSITIONS } from '../util/firstBookingDiscount';
import { identifyUserForPush } from '../util/pushNotifications';
import moment from 'moment';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const FETCH_CURRENT_USER_HAS_SALES_REQUEST = 'app/user/FETCH_CURRENT_USER_HAS_SALES_REQUEST';
export const FETCH_CURRENT_USER_HAS_SALES_SUCCESS = 'app/user/FETCH_CURRENT_USER_HAS_SALES_SUCCESS';
export const FETCH_CURRENT_USER_HAS_SALES_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_SALES_ERROR';

export const FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_REQUEST';
export const FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_SUCCESS';
export const FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  /** Al menos una transacción como proveedor (only: sale); null = aún no consultado */
  currentUserHasSales: null,
  currentUserHasSalesError: null,
  /**
   * El anfitrión ya ha recibido una reserva "real" (pagada/aceptada/etc., según
   * HOST_BOOKING_LAST_TRANSITIONS). Es la misma definición que usa el servidor
   * para el descuento de primera reserva. null = aún no consultado.
   */
  currentUserHasReceivedBooking: null,
  currentUserHasReceivedBookingError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserHasSales: null,
        currentUserHasSalesError: null,
        currentUserHasReceivedBooking: null,
        currentUserHasReceivedBookingError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case FETCH_CURRENT_USER_HAS_SALES_REQUEST:
      return { ...state, currentUserHasSalesError: null };
    case FETCH_CURRENT_USER_HAS_SALES_SUCCESS:
      return { ...state, currentUserHasSales: payload.hasSales };
    case FETCH_CURRENT_USER_HAS_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasSalesError: payload };

    case FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_REQUEST:
      return { ...state, currentUserHasReceivedBookingError: null };
    case FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_SUCCESS:
      return { ...state, currentUserHasReceivedBooking: payload.hasReceivedBooking };
    case FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasReceivedBookingError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasSalesRequest = () => ({ type: FETCH_CURRENT_USER_HAS_SALES_REQUEST });
const fetchCurrentUserHasSalesSuccess = hasSales => ({
  type: FETCH_CURRENT_USER_HAS_SALES_SUCCESS,
  payload: { hasSales },
});
const fetchCurrentUserHasSalesError = e => ({
  type: FETCH_CURRENT_USER_HAS_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasReceivedBookingRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_REQUEST,
});
const fetchCurrentUserHasReceivedBookingSuccess = hasReceivedBooking => ({
  type: FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_SUCCESS,
  payload: { hasReceivedBooking },
});
const fetchCurrentUserHasReceivedBookingError = e => ({
  type: FETCH_CURRENT_USER_HAS_RECEIVED_BOOKING_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      // Include drafts: user has listings if they have any listing (including draft)
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasListings));
      return response;
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

/** Reservas recibidas como anfitrión (al menos una transacción en modo sale). */
export const fetchCurrentUserHasSales = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasSalesRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasSalesSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'sale',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasSales = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasSalesSuccess(!!hasSales));
    })
    .catch(e => dispatch(fetchCurrentUserHasSalesError(storableError(e))));
};

/**
 * Whether the current user (as host/provider) has already received a "real"
 * booking — same definition the server uses to gate the first-booking discount
 * (HOST_BOOKING_LAST_TRANSITIONS). Unlike `fetchCurrentUserHasSales` (only:'sale',
 * any state), this excludes inquiries, declines, expiries and cancellations, so
 * the edit-listing offer gate matches server pricing and the listing-card badge.
 */
export const fetchCurrentUserHasReceivedBooking = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasReceivedBookingRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasReceivedBookingSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'sale',
    lastTransitions: HOST_BOOKING_LAST_TRANSITIONS,
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasReceivedBooking = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasReceivedBookingSuccess(!!hasReceivedBooking));
    })
    .catch(e => dispatch(fetchCurrentUserHasReceivedBookingError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  // const currentUser = getState().user.currentUser;
  // const userRole =
  //   (currentUser && currentUser.id && currentUser.attributes.profile.publicData.userType) || '';
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  // const onlyVar = userRole == CUSTOMER ? 'order' : 'sale';

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    include: ['customer'],
    'fields.user': ['banned', 'deleted'],
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const included = response.data.included || [];
      // Drop transactions whose customer has been blocked (banned or deleted) so
      // the host notification badge matches the (filtered) inbox list.
      const blockedUserIds = new Set(
        included
          .filter(r => r.type === 'user' && (r.attributes?.banned || r.attributes?.deleted))
          .map(r => r.id.uuid)
      );
      const transactions = response.data.data.filter(
        tx => !blockedUserIds.has(tx.relationships?.customer?.data?.id?.uuid)
      );
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      // sessionStorage.removeItem('SearchListing');
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];
      const userType =
        currentUser && currentUser.id && currentUser?.attributes?.profile?.publicData?.userType;

      //commented metapixel on fetchcurrent user, need to discuss with yugam
      // userType == PROVIDER ? fbq.event('Lead', {content_category: "host"}) : fbq.event('Lead', {content_category: "renter"});

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }
      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      // Native app only: tell OneSignal which user this device belongs to, so a push can
      // be addressed to a person. This is the one funnel every authentication path goes
      // through (the IdP flows are full-page redirects that never run the login thunk).
      // No-op in a browser and during SSR; deduped and silent on failure.
      identifyUserForPush(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }
      const userType = currentUser?.attributes?.profile?.publicData?.userType;
      if (userType === PROVIDER && getState().user.currentUserHasSales === null) {
        dispatch(fetchCurrentUserHasSales());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
